import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { devError } from '../utils/logger';

const FavoritesContext = createContext({
  ids: new Set(),
  isFavorite: () => false,
  toggle: async () => {},
  refresh: async () => {},
  ready: false,
});

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setIds(new Set());
      setReady(true);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/favorites/ids`);
      setIds(new Set(data.listing_ids || []));
    } catch (e) {
      devError('favorites refresh failed', e);
      setIds(new Set());
    } finally {
      setReady(true);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggle = useCallback(
    async (listingId) => {
      if (!user) return { needsAuth: true };
      const has = ids.has(listingId);
      // optimistic
      setIds((prev) => {
        const next = new Set(prev);
        if (has) next.delete(listingId);
        else next.add(listingId);
        return next;
      });
      try {
        if (has) {
          await axios.delete(`${API}/favorites/${listingId}`);
        } else {
          await axios.post(`${API}/favorites/${listingId}`);
        }
        return { favorited: !has };
      } catch (e) {
        // rollback
        setIds((prev) => {
          const next = new Set(prev);
          if (has) next.add(listingId);
          else next.delete(listingId);
          return next;
        });
        devError('favorites toggle failed', e);
        return { error: true };
      }
    },
    [ids, user],
  );

  const isFavorite = useCallback((listingId) => ids.has(listingId), [ids]);

  const value = { ids, isFavorite, toggle, refresh, ready };
  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export const useFavorites = () => useContext(FavoritesContext);
